import { DestroyRef, Inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ErrorEvent, EventSource, EventSourceFetchInit } from "eventsource";
import { EMPTY, Observable, Subject, Subscriber, fromEvent } from "rxjs";
import { SSEEventTypesEnum } from "../models/detail-ost/enums/sse-event-types.enum";
import { OstUploadSuccessEvent } from "../models/images/ost-upload-tracking.model";
import { IEnvironment } from "../models/interfaces/environment.interface";
import { ManifestSseEvent } from "../models/manifest/manifest-sse-event.model";
import { OST_EVENT_SSE_EVENT_TYPES, OstEvent, OstSseEventModel, isOstEventSseEventType, parseOstEventStreamPayload } from "../models/ost/events/ost-sse-event.model";
import { OstResponse } from "../models/ost/ost-response.model";
import { ENVIRONMENT } from "../modules/keycloak.module";
import { OstStore } from "../stores/ost.store";
import { UserStore } from "../stores/user.store";

const OST_SSE_EVENT_TYPES = [
  ...OST_EVENT_SSE_EVENT_TYPES,
  SSEEventTypesEnum.GIFTCARD_STATUS_CHANGED,
  SSEEventTypesEnum.OST_FILE_IMAGE_UPLOADED,
] as const satisfies readonly SSEEventTypesEnum[];

type OstSseEventType = (typeof OST_SSE_EVENT_TYPES)[number];
type OstListSseEventType = (typeof OST_EVENT_SSE_EVENT_TYPES)[number];
type OstSseEventHandler = (event: OstSseEventModel) => void;

@Injectable({
  providedIn: "root",
})
export class EventStreamService {
  private eventSource?: EventSource;
  private connectingOst = false;
  private connectionGeneration = 0;
  private storeEventSource?: EventSource;
  readonly messageToShow = signal<string | undefined>(undefined);

  private readonly manifestCreatedSubject = new Subject<ManifestSseEvent>();
  readonly manifestCreated$ = this.manifestCreatedSubject.asObservable();

  private readonly giftcardStatusChangedSubject = new Subject<void>();
  readonly giftcardStatusChanged$ = this.giftcardStatusChangedSubject.asObservable();

  private readonly uploadedFileSuccessSubject = new Subject<OstUploadSuccessEvent>();
  readonly uploadedFileSuccess$ = this.uploadedFileSuccessSubject.asObservable();

  private readonly ostSseEventHandlers: Record<OstSseEventType, OstSseEventHandler> = {
    [SSEEventTypesEnum.F11_NUM]: (event: OstSseEventModel): void => this.updateSelectedOstFromEvent({ f11Num: event.f11Num }, "Número de F11 actualizado"),
    [SSEEventTypesEnum.GUIA_DESPACHO]: (event: OstSseEventModel): void =>
      this.updateSelectedOstFromEvent({ f11NumeroGuiaDespacho: event.nGuiaDespacho }, "Número de guía de despacho actualizado"),
    [SSEEventTypesEnum.OST_STATE]: (event: OstSseEventModel): void => this.updateSelectedOstFromEvent({ ostEstado: event.ostEstado }, "Estado de OST actualizado"),
    [SSEEventTypesEnum.OST_UPDATED_SUB_STATE]: (event: OstSseEventModel): void =>
      this.updateSelectedOstFromEvent({ ostSubestado: event.ostSubestado }, "SubEstado de OST actualizado"),
    [SSEEventTypesEnum.PRODUCT_STATE]: (event: OstSseEventModel): void =>
      this.updateSelectedOstFromEvent({ ostEstadoGestionProducto: event.ostEstadoGestionProducto }, "Estado de gestión de producto actualizado"),
    [SSEEventTypesEnum.F11_STATE]: (event: OstSseEventModel): void => this.updateSelectedOstFromEvent({ f11Estado: event.f11Estado }, "Estado de F11 actualizado"),
    [SSEEventTypesEnum.OST_IS_F11_EMPRESA]: (event: OstSseEventModel): void =>
      this.updateSelectedOstFromEvent({ ostIsF11Empresa: event.ostIsF11Empresa }, "Tipo de F11 actualizado"),
    [SSEEventTypesEnum.LABEL_CREATED]: (): void => this.updateSelectedOstFromEvent({ etiquetaCreada: true }, "Recuerda descargar la etiqueta de correos de chile"),
    [SSEEventTypesEnum.GIFTCARD_STATUS_CHANGED]: (): void => this.giftcardStatusChangedSubject.next(),
    [SSEEventTypesEnum.OST_FILE_IMAGE_UPLOADED]: (): void => undefined,
  };

  constructor(
    @Inject(ENVIRONMENT) private readonly environment: IEnvironment,
    private readonly ostStore: OstStore,
    private readonly userStore: UserStore,
    private readonly destroyRef: DestroyRef
  ) {
    fromEvent(window, "beforeunload")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.closeConnection());
  }

  private createEventSourceInstance(url: string): EventSource {
    const headersConfig = this.environment.APP_CONFIGURATION.HEADERS;
    const currentUser = this.userStore.user();

    return new EventSource(url, {
      fetch: (input: string | URL, init?: EventSourceFetchInit): Promise<Response> =>
        fetch(input, {
          ...init,
          headers: {
            Accept: "text/event-stream",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            "X-Api-Key": this.environment.APIKEY,
            "X-Chref": headersConfig["CHREF"],
            "X-Commerce": headersConfig["COMMERCE"],
            "x-cmref": headersConfig["CMREF"],
            "x-country": currentUser?.country?.toUpperCase() ?? "",
            "x-no-compression": "true",
            Authorization: `Bearer ${this.userStore.jwt()}`,
          },
        }),
    });
  }

  private createEventSource(url: string): Promise<EventSource> {
    return new Promise<EventSource>((resolve: (value: EventSource | PromiseLike<EventSource>) => void, reject: (reason?: unknown) => void): void => {
      let isOpen = false;
      const es = this.createEventSourceInstance(url);

      es.onopen = (): void => {
        isOpen = true;
        resolve(es);
      };

      es.onerror = (err: ErrorEvent): void => {
        if (!isOpen) {
          es.close();
          reject(err);
        }
      };
    });
  }

  private buildNotificationStreamUrl(notificationId: string | number, query = ""): string {
    const { BASE, NOTIFICATIONS, STREAM }: Record<string, string> = this.environment.SEE;
    return `${this.environment.BASE_URL}${BASE}${NOTIFICATIONS}/${notificationId}${STREAM}${query}`;
  }

  private hearOstNotifications(ostNum: number): Promise<EventSource> {
    const query = ostNum === 0 ? "?allEvents=true" : "";
    return this.createEventSource(this.buildNotificationStreamUrl(ostNum, query));
  }

  private buildOstListNotificationStreamUrl(ostNums: readonly number[]): string {
    return this.buildNotificationStreamUrl(ostNums.join(","));
  }

  listenCurrentPageOstEvents(ostNums: readonly (number | string)[]): Observable<OstEvent> {
    const uniqueOstNums = this.normalizeOstNums(ostNums);

    if (uniqueOstNums.length === 0) {
      return EMPTY;
    }

    return new Observable<OstEvent>((subscriber: Subscriber<OstEvent>) => {
      const eventSource = this.createEventSourceInstance(this.buildOstListNotificationStreamUrl(uniqueOstNums));
      const eventListeners = OST_EVENT_SSE_EVENT_TYPES.map((eventType: OstListSseEventType) => {
        const listener = (event: MessageEvent<string>): void => {
          const ostEvent = this.parseOstListEvent(event.data, eventType);

          if (!ostEvent || !isOstEventSseEventType(String(ostEvent.eventType))) {
            return;
          }

          subscriber.next(ostEvent);
        };

        eventSource.addEventListener(eventType, listener);
        return { eventType, listener };
      });
      const errorListener = (event: ErrorEvent): void => {
        console.error("Error escuchando eventos SSE de la página actual de OST:", event);
        eventSource.close();
        subscriber.complete();
      };

      eventSource.addEventListener("error", errorListener);

      return (): void => {
        eventListeners.forEach(({ eventType, listener }: { eventType: OstListSseEventType; listener: (event: MessageEvent<string>) => void }): void => {
          eventSource.removeEventListener(eventType, listener);
        });
        eventSource.removeEventListener("error", errorListener);
        eventSource.close();
      };
    });
  }

  listenUploadSuccessEvents(ostNums: readonly (number | string)[]): Observable<OstUploadSuccessEvent> {
    const uniqueOstNums = this.normalizeOstNums(ostNums);
    if (uniqueOstNums.length === 0) return EMPTY;
    const subscribedOstNums = new Set(uniqueOstNums.map((ostNum: number): string => String(ostNum)));

    return new Observable<OstUploadSuccessEvent>((subscriber: Subscriber<OstUploadSuccessEvent>) => {
      const eventSource = this.createEventSourceInstance(this.buildOstListNotificationStreamUrl(uniqueOstNums));
      const listener = (event: MessageEvent<string>): void => {
        const uploadEvent = this.parseUploadSuccessEvent(event.data);
        if (uploadEvent && subscribedOstNums.has(uploadEvent.ostNum)) {
          subscriber.next(uploadEvent);
        }
      };

      eventSource.addEventListener(SSEEventTypesEnum.OST_FILE_IMAGE_UPLOADED, listener);
      return (): void => {
        eventSource.removeEventListener(SSEEventTypesEnum.OST_FILE_IMAGE_UPLOADED, listener);
        eventSource.close();
      };
    });
  }

  private normalizeOstNums(ostNums: readonly (number | string)[]): number[] {
    return [...new Set(ostNums.map((ostNum: number | string): number => Number(ostNum)).filter((ostNum: number): boolean => Number.isInteger(ostNum) && ostNum > 0))];
  }

  private parseOstListEvent(data: string, fallbackEventType?: string): OstEvent | undefined {
    return parseOstEventStreamPayload(data, fallbackEventType);
  }

  async startConnectionAllEvents(ostNumber: number): Promise<void> {
    const generation = this.connectionGeneration;
    this.connectingOst = true;
    try {
      const eventSource = await this.hearOstNotifications(ostNumber);
      // closeConnection() ran while connecting: drop this source instead of leaking an open SSE.
      if (generation !== this.connectionGeneration) {
        eventSource.close();
        return;
      }
      this.addEventsToEventSource(eventSource);
      this.eventSource = eventSource;
    } finally {
      if (generation === this.connectionGeneration) this.connectingOst = false;
    }
  }

  private addEventsToEventSource(eventSource: EventSource): void {
    for (const eventType of OST_SSE_EVENT_TYPES) {
      eventSource.addEventListener(eventType, (event: MessageEvent<string>): void => {
        this.handleSseEvent(event);
      });
    }
  }

  private handleSseEvent(eventSee: MessageEvent<string>): void {
    let event: OstSseEventModel;
    try {
      event = JSON.parse(eventSee.data) as OstSseEventModel;
    } catch {
      return;
    }

    if (!this.isOstSseEventType(event.eventType)) {
      return;
    }

    if (event.eventType === SSEEventTypesEnum.OST_FILE_IMAGE_UPLOADED) {
      const uploadEvent = this.parseUploadSuccessEvent(eventSee.data);
      if (uploadEvent) this.uploadedFileSuccessSubject.next(uploadEvent);
      return;
    }

    this.ostSseEventHandlers[event.eventType](event);
  }

  private parseUploadSuccessEvent(data: string): OstUploadSuccessEvent | undefined {
    try {
      const event = JSON.parse(data) as unknown;
      if (!event || typeof event !== "object" || Array.isArray(event)) return undefined;
      const payload = event as Record<string, unknown>;
      if (payload["eventType"] !== undefined && payload["eventType"] !== String(SSEEventTypesEnum.OST_FILE_IMAGE_UPLOADED)) return undefined;

      const ostNum = typeof payload["ostNum"] === "number" ? String(payload["ostNum"]) : typeof payload["ostNum"] === "string" ? payload["ostNum"].trim() : "";
      // File fields arrive nested: { ostNum, eventType, manifest: { fileName } }.
      const manifest = payload["manifest"] && typeof payload["manifest"] === "object" ? (payload["manifest"] as Record<string, unknown>) : {};
      const text = (key: string): string => {
        const value = manifest[key] ?? payload[key];
        return typeof value === "string" ? value.trim() : "";
      };
      const fileName = text("fileName");
      if (!/^\d+$/.test(ostNum) || Number(ostNum) <= 0 || !fileName) return undefined;
      return { ostNum, fileName, fechaGeneracion: text("fechaGeneracion") };
    } catch {
      return undefined;
    }
  }

  private isOstSseEventType(eventType: string): eventType is OstSseEventType {
    return OST_SSE_EVENT_TYPES.some((ostSseEventType: OstSseEventType): boolean => String(ostSseEventType) === eventType);
  }

  private updateSelectedOstFromEvent(changes: Partial<OstResponse>, message: string): void {
    this.ostStore.setSelectedOstFromTable({
      ...this.ostStore.selectedOstFromTable(),
      ...changes,
    });
    this.messageToShow.set(message);
  }

  async startStoreConnection(storeCode: string, retries = 3, delay = 2000): Promise<void> {
    this.closeStoreConnection();
    await this.connectStoreEventSource(this.buildNotificationStreamUrl(`S-${storeCode}`), storeCode, retries, delay);
  }

  private async connectStoreEventSource(url: string, storeCode: string, retries: number, delay: number, attempt = 1): Promise<void> {
    try {
      const eventSource = await this.createEventSource(url);
      eventSource.addEventListener(SSEEventTypesEnum.MANIFEST_CREATED, (event: MessageEvent<string>): void => {
        this.handleManifestCreatedEvent(event, storeCode);
      });
      this.storeEventSource = eventSource;
    } catch (error: unknown) {
      if (attempt >= retries) {
        throw error;
      }

      await this.wait(delay * attempt);
      return this.connectStoreEventSource(url, storeCode, retries, delay, attempt + 1);
    }
  }

  private handleManifestCreatedEvent(eventSee: MessageEvent<string>, storeCode: string): void {
    const event = JSON.parse(eventSee.data) as ManifestSseEvent;

    if (event.eventType === String(SSEEventTypesEnum.MANIFEST_CREATED) && event.manifest.idStore === storeCode) {
      this.manifestCreatedSubject.next(event);
    }
  }

  private wait(milliseconds: number): Promise<void> {
    return new Promise<void>((resolve: () => void): void => {
      setTimeout(resolve, milliseconds);
    });
  }

  closeStoreConnection(): void {
    this.storeEventSource?.close();
    this.storeEventSource = undefined;
  }

  isClosed(): boolean {
    if (this.connectingOst) return false;
    const rs = this.eventSource?.readyState;
    return rs === undefined || rs === EventSource.CLOSED;
  }

  closeConnection(): void {
    this.connectionGeneration++;
    this.connectingOst = false;
    this.eventSource?.close();
    this.eventSource = undefined;
    this.messageToShow.set(undefined);
    this.closeStoreConnection();
  }
}
